import { getSubjectLabels } from '@/lib/settings';
import React from 'react';
import { Search, User } from 'lucide-react';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import type { LehrerOption } from '../../lib/unterrichtseinheiten';

type ScheduleStatus = 'aktiv' | 'geplant' | 'verschoben' | 'abgesagt' | 'durchgeführt' | 'nicht_erschienen' | 'abgeschlossen';
export type GroupParticipantFilter = 'all' | '6plus' | '10plus';

type Props = {
  teacherLabel: string;
  teacherOptions: LehrerOption[];
  selectedTeacher?: string | number;
  onTeacherChange: (value: string) => void;
  searchQuery: string;
  onSearchQueryChange: (value: string) => void;
  showTrials: boolean;
  onShowTrialsChange: (value: boolean) => void;
  showExternalBlocked: boolean;
  onShowExternalBlockedChange: (value: boolean) => void;
  showOnlyGroupCourses: boolean;
  onShowOnlyGroupCoursesChange: (value: boolean) => void;
  groupParticipantFilter: GroupParticipantFilter;
  onGroupParticipantFilterChange: (value: GroupParticipantFilter) => void;
  activeStatuses: Record<ScheduleStatus, boolean>;
  onToggleStatus: (status: ScheduleStatus) => void;
  onResetFilters: () => void;
};

const STATUS_LABELS: Array<{ key: ScheduleStatus; label: string }> = [
  { key: 'aktiv', label: 'Aktiv' },
  { key: 'geplant', label: 'Geplant' },
  { key: 'verschoben', label: 'Verschoben' },
  { key: 'abgeschlossen', label: 'Abgeschlossen' },
  { key: 'abgesagt', label: 'Abgesagt' },
  { key: 'durchgeführt', label: 'Durchgeführt' },
  { key: 'nicht_erschienen', label: 'Nicht erschienen' },
];

const ScheduleToolbar: React.FC<Props> = ({
  teacherLabel,
  teacherOptions,
  selectedTeacher,
  onTeacherChange,
  searchQuery,
  onSearchQueryChange,
  showTrials,
  onShowTrialsChange,
  showExternalBlocked,
  onShowExternalBlockedChange,
  showOnlyGroupCourses,
  onShowOnlyGroupCoursesChange,
  groupParticipantFilter,
  onGroupParticipantFilterChange,
  activeStatuses,
  onToggleStatus,
  onResetFilters,
}) => {
  return (
    <div className="rounded-lg border bg-card p-3 space-y-3">
      <div className="grid gap-3 md:grid-cols-[260px_1fr_auto] md:items-center">
        <div className="flex items-center gap-2">
          <User className="h-5 w-5 text-muted-foreground" />
          <span className="text-sm font-medium">{teacherLabel}:</span>
          <Select value={selectedTeacher?.toString()} onValueChange={onTeacherChange}>
            <SelectTrigger className="w-44">
              <SelectValue placeholder={`${teacherLabel} auswählen`} />
            </SelectTrigger>
            <SelectContent>
              {teacherOptions.length === 0 ? (
                <SelectItem value="__none__" disabled>
                  Keine {teacherLabel} verfügbar
                </SelectItem>
              ) : (
                teacherOptions.map((t) => (
                  <SelectItem key={t.id} value={String(t.id)}>
                    {t.vorname} {t.nachname}
                  </SelectItem>
                ))
              )}
            </SelectContent>
          </Select>
        </div>

        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={searchQuery}
            onChange={(e) => onSearchQueryChange(e.target.value)}
            placeholder={`Suche nach Kurs, ${getSubjectLabels().singular}, Teilnehmer oder Raum`}
            className="pl-9"
          />
        </div>

        <Button type="button" variant="outline" onClick={onResetFilters}>
          Filter zurücksetzen
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {STATUS_LABELS.map((status) => (
          <Button
            key={status.key}
            type="button"
            size="sm"
            variant={activeStatuses[status.key] ? 'secondary' : 'outline'}
            onClick={() => onToggleStatus(status.key)}
          >
            {status.label}
          </Button>
        ))}

        <div className="ml-auto flex flex-wrap items-center gap-4">
          <label className="flex items-center gap-2 text-sm">
            <Checkbox checked={showTrials} onCheckedChange={(checked) => onShowTrialsChange(checked === true)} />
            Probestunden
          </label>
          <label className="flex items-center gap-2 text-sm">
            <Checkbox checked={showExternalBlocked} onCheckedChange={(checked) => onShowExternalBlockedChange(checked === true)} />
            Externe Blocker
          </label>
          <label className="flex items-center gap-2 text-sm">
            <Checkbox checked={showOnlyGroupCourses} onCheckedChange={(checked) => onShowOnlyGroupCoursesChange(checked === true)} />
            Nur Gruppenkurse
          </label>
          <div className="flex items-center gap-2 text-sm">
            <span className="text-muted-foreground">Teilnehmer:</span>
            <Select
              value={groupParticipantFilter}
              onValueChange={(value) => onGroupParticipantFilterChange(value as GroupParticipantFilter)}
            >
              <SelectTrigger className="h-8 w-28">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle</SelectItem>
                <SelectItem value="6plus">Ab 6</SelectItem>
                <SelectItem value="10plus">Ab 10</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>
    </div>
  );
};

export type { ScheduleStatus };
export default ScheduleToolbar;

